import React, { useRef, useState, useMemo, useEffect, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useSupercluster from 'use-supercluster';
import { Row, Col } from 'react-bootstrap';
import { Maximize, Minimize } from 'lucide-react';
import { Text } from 'v3/components';
import theme from 'v3/theme';
import { FullscreenButton, MapsContainer } from './styles';
import MapDetailCard from './MapDetailCard';
import apiReport from 'services/apis/report';
import { useSnackbar } from 'v3/components/Snackbar';

const DEFAULT_CENTER = [-14.235, -51.9253];
const DEFAULT_ZOOM = 4;

function createPinIcon(background, sizeIcon = 22, color = '#fff') {
  const isSvg = String(background).includes('.svg');
  const backgroundCss = isSvg
    ? `background-image: url(/images/${background}); background-size: 100% 100%;`
    : `background: ${background};`;

  return L.divIcon({
    className: '',
    iconSize: [sizeIcon, sizeIcon],
    iconAnchor: [sizeIcon / 2, sizeIcon],
    html: `
      <div style="
        width: ${sizeIcon}px;
        height: ${sizeIcon}px;
        border-radius: 0% 50% 50% 50%;
        ${backgroundCss}
        transform: rotate(-135deg);
        transform-origin: top left;
        position: absolute;
        left: 50%;
        top: 50%;
        margin: 0;
      ">
        <div style="
          width: 14px;
          height: 14px;
          margin: 10px 0 0 10px;
          transform: rotate(135deg);
          border-radius: 50%;
          color: ${color};
        "></div>
      </div>
    `,
  });
}

function createClusterIcon(pointCount, totalPoints) {
  const iconSize = 10 + (pointCount / Math.max(totalPoints, 1)) * 20;
  return L.divIcon({
    className: '',
    iconSize: [iconSize, iconSize],
    iconAnchor: [iconSize / 2, iconSize / 2],
    html: `
      <div style="
        width: ${iconSize}px;
        height: ${iconSize}px;
        color: #fff;
        background: #1978c8;
        border-radius: 50%;
        padding: 10px;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 12px;
        font-weight: 700;
      ">${pointCount}</div>
    `,
  });
}

function MapEventsHandler({ setBounds, setZoom, setChecked, mapRef }) {
  const map = useMapEvents({
    click: () => setChecked(false),
    zoomend: () => {
      const mapBounds = map.getBounds();
      setZoom(map.getZoom());
      setBounds([mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()]);
    },
    moveend: () => {
      const mapBounds = map.getBounds();
      setBounds([mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()]);
    },
  });

  useEffect(() => {
    mapRef.current = map;
    const mapBounds = map.getBounds();
    setZoom(map.getZoom());
    setBounds([mapBounds.getWest(), mapBounds.getSouth(), mapBounds.getEast(), mapBounds.getNorth()]);
  }, [map, mapRef, setBounds, setZoom]);

  return null;
}

function ResizeController({ trigger }) {
  const map = useMap();

  useEffect(() => {
    if (!map) return undefined;

    map.invalidateSize(true);

    const timeout = setTimeout(() => {
      map.invalidateSize(true);
    }, 180);

    return () => clearTimeout(timeout);
  }, [map, trigger]);

  return null;
}

export default function MapDrivers({
  driversData,
  showMap,
  loading,
  checked,
  setChecked,
  locatedUpTo,
}) {
  const snackbar = useSnackbar();
  const [detail, setDetail] = useState([]);
  const mapRef = useRef();
  const mapContainerRef = useRef(null);
  const [bounds, setBounds] = useState(null);
  const [zoom, setZoom] = useState(0);
  const [fullScreen, setFullScreen] = useState(false);

  function parseCoordinate(value) {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const normalized = String(value).replace(',', '.').trim();
    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  const points = useMemo(() => {
    if (driversData) {
      const pDriver = driversData
        ?.map((driver) => {
          const latitude = parseCoordinate(driver?.latitude ?? driver?.lat);
          const longitude = parseCoordinate(driver?.longitude ?? driver?.lng ?? driver?.lon);

          if (latitude === null || longitude === null) return null;

          return {
            type: 'Feature',
            properties: {
              cluster: false,
              id: driver.id,
              category: 'light_blue',
            },
            geometry: {
              type: 'Point',
              coordinates: [longitude, latitude],
            },
          };
        })
        .filter(Boolean);
      return [...pDriver];
    } 
      return [];
    
  }, [driversData]);
  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: bounds || [-180, -85, 180, 85],
    zoom: Number.isFinite(zoom) ? Math.round(zoom) : DEFAULT_ZOOM,
    options: { radius: 75, minZoom: 0, maxZoom: 20, minPoints: 2 },
  });

  useEffect(() => {
    if (!mapRef.current || !points.length) return;

    const leafletBounds = L.latLngBounds(
      points.map((point) => [point.geometry.coordinates[1], point.geometry.coordinates[0]]),
    );

    mapRef.current.fitBounds(leafletBounds, { padding: [30, 30] });
  }, [points]);

  const toggleFullScreen = useCallback(() => {
    const element = mapContainerRef.current;
    if (!element) return;

    const fullscreenElement =
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement;

    if (fullscreenElement) {
      const exit =
        document.exitFullscreen ||
        document.webkitExitFullscreen ||
        document.mozCancelFullScreen ||
        document.msExitFullscreen;

      if (exit) exit.call(document);
      return;
    }

    const enter =
      element.requestFullscreen ||
      element.webkitRequestFullscreen ||
      element.mozRequestFullScreen ||
      element.msRequestFullscreen;

    if (enter) enter.call(element);
  }, []);

  useEffect(() => {
    function handleFullScreenChange() {
      const fullscreenElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      setFullScreen(fullscreenElement === mapContainerRef.current);
    }

    document.addEventListener('fullscreenchange', handleFullScreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullScreenChange);
    document.addEventListener('mozfullscreenchange', handleFullScreenChange);
    document.addEventListener('MSFullscreenChange', handleFullScreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullScreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullScreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullScreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullScreenChange);
    };
  }, []);

  async function retrieveDriver(id) {
    try {
      const response = await apiReport.get(
        `/drivers/${id}`
      );
      setDetail(response.data);
      setChecked(true);
    } catch(err) {
      snackbar.show(<Text>Erro ao recuperar motoristas.</Text>, {
        type: 'error',
      });
      setDetail(null);
    }
  }

  const closeDriverDetail = useCallback(() => {
    setChecked(false);
    setDetail([]);
  }, [setChecked]);

  useEffect(() => {
    if (!checked) return undefined;

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        closeDriverDetail();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [checked, closeDriverDetail]);

  function handleChangeColor(category) {
    const colorMap = {
      light_blue: theme.colors.light_blue,
    };
    return colorMap[category] || '';
  }
  return (
    <MapsContainer>
      <>
        {showMap === true && loading === false ? (
          <>
            <Row>
              <Col>
                <Text type="regular" weight={500}>
                    {`Motoristas localizados ${ locatedUpTo === 'today' ? 'hoje' : 'até 3 dias'}`}
                </Text>
              </Col>
            </Row>
            <Row>
              <Col>
                <div
                  ref={mapContainerRef}
                  style={{
                    height: fullScreen ? '100vh' : '50vh',
                    position: 'relative',
                    width: '100%',
                  }}
                >
                  <FullscreenButton
                    aria-label={fullScreen ? 'Sair da tela cheia' : 'Ver em tela cheia'}
                    title={fullScreen ? 'Sair da tela cheia' : 'Tela cheia'}
                    type="button"
                    onClick={toggleFullScreen}
                  >
                    {fullScreen ? <Minimize size={18} /> : <Maximize size={18} />}
                  </FullscreenButton>
                  <MapContainer
                    center={DEFAULT_CENTER}
                    zoom={DEFAULT_ZOOM}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom
                  >
                    <TileLayer
                      attribution='&copy; OpenStreetMap contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapEventsHandler
                      setBounds={setBounds}
                      setZoom={setZoom}
                      setChecked={setChecked}
                      mapRef={mapRef}
                    />
                    <ResizeController trigger={fullScreen} />
                    {clusters.map((cluster) => {
                      const [longitude, latitude] = cluster.geometry.coordinates;
                      const {
                        cluster: isCluster,
                        point_count: pointCount,
                        category,
                      } = cluster.properties;
                      if (isCluster) {
                        return (
                          <Marker
                            key={`cluster-${cluster.id}`}
                            position={[latitude, longitude]}
                            icon={createClusterIcon(pointCount, points?.length)}
                            eventHandlers={{
                              click: () => {
                                const expansionZoom = Math.min(
                                  supercluster.getClusterExpansionZoom(cluster.id),
                                  20,
                                );
                                mapRef.current.setView([latitude, longitude], expansionZoom);
                              },
                            }}
                          />
                        );
                      }
                      return (
                        <Marker
                          key={cluster.properties.id}
                          position={[latitude, longitude]}
                          icon={createPinIcon(handleChangeColor(category), 22, '#fff')}
                          eventHandlers={{
                            click: () => {
                              retrieveDriver(cluster.properties.id);
                            },
                          }}
                        />
                      );
                    })}
                  </MapContainer>
                  {points.length === 0 && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        zIndex: 400,
                        backgroundColor: '#fff',
                        padding: '8px 10px',
                        borderRadius: '6px',
                        boxShadow: '0 1px 4px rgba(0,0,0,0.2)',
                        maxWidth: 'calc(100% - 80px)',
                      }}
                    >
                      <Text type="regular" weight={500} fontSize="0.75rem">
                        Nenhum motorista com coordenada para o filtro selecionado.
                      </Text>
                    </div>
                  )}
                  <Col>
                    {checked ? (
                      <>
                        <MapDetailCard
                          driver={detail}
                          checked={checked}
                          onClose={closeDriverDetail}
                         />
                      </>
                    ) : null}
                  </Col>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <></>
        )}
      </>
    </MapsContainer>
  );
}
